import { useEffect } from 'react';
import type { Movie } from '@/types';

interface PlaybackModalProps {
  open: boolean;
  movie?: Movie;
  onClose: () => void;
}

export default function PlaybackModal({ open, movie, onClose }: PlaybackModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background-50/85 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="playback-title"
      onClick={onClose}
    >
      <div
        className="animate-fade-up w-full max-w-3xl overflow-hidden rounded-lg border border-background-300/70 bg-background-100"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-background-300/60 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-6 items-center rounded bg-primary-500/15 px-2 text-[11px] font-medium text-primary-500">
              演示模式
            </span>
            <h2 id="playback-title" className="truncate text-sm text-foreground-800">
              {movie ? `《${movie.title}》` : '播放预览'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭播放提示"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        <div className="relative aspect-video w-full overflow-hidden bg-background-50">
          {movie?.backdrop && (
            <img
              src={movie.backdrop}
              alt={`《${movie.title}》剧照`}
              className="h-full w-full object-cover opacity-35"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/20" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-primary-500/40 bg-primary-500/20 text-primary-500 md:h-20 md:w-20">
              <i className="ri-play-fill pl-1 text-3xl md:text-4xl" aria-hidden="true"></i>
            </span>
            <p className="mt-5 font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">
              {movie ? movie.title : '影片预览'}
            </p>
            <p className="mt-2 text-xs text-foreground-500 md:text-sm">
              {movie ? `${movie.year} · ${movie.region} · ${movie.genres.join(' / ')}` : '前端演示播放器'}
            </p>
            <div className="mt-6 h-1 w-full max-w-md overflow-hidden rounded-full bg-background-300/60">
              <div className="h-full w-1/6 rounded-full bg-primary-500/60" />
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5 md:px-6">
          <div className="flex gap-3 rounded-md border border-primary-500/25 bg-primary-500/10 p-4">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-primary-500">
              <i className="ri-information-line text-sm" aria-hidden="true"></i>
            </span>
            <div className="text-sm leading-6 text-foreground-700">
              <p className="font-medium text-foreground-900">这是前端演示，不包含真实播放</p>
              <p className="mt-1 text-foreground-500">
                午夜电影网目前为演示站点，未接入任何真实片源、播放地址或下载服务。点击播放不会开始加载视频，也不会产生播放记录。
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-foreground-500/60 hover:text-foreground-900"
            >
              返回详情
            </button>
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              我知道了
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}